import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import anime from "animejs";
import { listRiders, deleteRider, updateRider, batchApproveRiders, batchDeleteRiders, downloadOriginalRiderPdf, type RiderListItem } from "@/api/client";
import { toast } from "sonner";
import { useActiveOrgId } from "@/hooks/useActiveOrg";
import { RidersWorkspaceHeader } from "@/components/riders/RidersWorkspaceHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { ProcessingHeader } from "@/components/upload/ProcessingHeader";
import { ProcessingItem } from "@/components/upload/ProcessingItem";
import type { ProcessingItemData } from "@/components/upload/types";
import { Button, Input, Segmented, ConfidenceChip, ConfirmDialog, StatusBadge, EmptyState, type RiderStatus } from "@/components/ds";
import { Trash2, Pencil, Check, CheckCheck, X, Search, ArrowUpDown, ArrowUp, ArrowDown, FileText, Download } from "lucide-react";
import {
  isRiderProcessing,
  matchesRiderStatusFilter,
  riderExtractionStep,
  riderLifecycleStatus,
} from "@/lib/riderLifecycle";

type SortKey = "artist_name" | "rider_type" | "status" | "confidence" | "created_at";
type SortDir = "asc" | "desc";

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "extracting", label: "Processing" },
  { value: "extracted", label: "Need review" },
  { value: "approved", label: "Approved" },
  { value: "failed", label: "Failed" },
];

// Map the rider lifecycle to the DS five status classes (extracting → processing).
const RIDER_STATUS_MAP: Record<string, { s: RiderStatus; label: string }> = {
  pending: { s: "pending", label: "Pending" },
  processing: { s: "processing", label: "Processing" },
  extracting: { s: "processing", label: "Extracting" },
  extracted: { s: "extracted", label: "Extracted" },
  approved: { s: "approved", label: "Approved" },
  failed: { s: "failed", label: "Failed" },
};

function RiderStatusBadge({ status }: { status: string }) {
  const m = RIDER_STATUS_MAP[status] ?? { s: "pending" as RiderStatus, label: status };
  return <StatusBadge status={m.s} label={m.label} />;
}

function riderDisplayStatus(rider: RiderListItem): string {
  return riderLifecycleStatus(
    rider.status,
    rider.confidence_scores,
    rider.extraction_step,
  );
}

function riderToProcessingItem(r: RiderListItem): ProcessingItemData {
  return {
    id: r.id,
    artistName: r.artist_name,
    status: isRiderProcessing(
      r.status,
      r.confidence_scores,
      r.extraction_step,
    )
      ? "extracting"
      : r.status as ProcessingItemData["status"],
    extractionStep: riderExtractionStep(
      r.confidence_scores,
      r.extraction_step,
    ),
  };
}

export function RiderListPage() {
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") ?? "";
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  // Delete confirmation (single or bulk). Target outlives `open` so the
  // dialog content doesn't blank out during the close fade.
  const [deleteTarget, setDeleteTarget] = useState<{ ids: string[]; names: string[] } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const animatedRef = useRef(false);

  const [elapsed, setElapsed] = useState("00:00");
  const extractingStartRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const queryClient = useQueryClient();
  const activeOrgId = useActiveOrgId();

  const { data: riders, isLoading, isFetching } = useQuery({
    queryKey: ["riders", activeOrgId],
    // Lifecycle filters are grouped client-side. A raw `status=extracting`
    // request would omit pending jobs and extracted rows finishing Hospitality.
    queryFn: () => listRiders(undefined, activeOrgId),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        data?.some((r) =>
          isRiderProcessing(
            r.status,
            r.confidence_scores,
            r.extraction_step,
          ),
        )
      ) return 3000;
      return false;
    },
  });

  const processingRiders = useMemo(
    () =>
      (riders ?? []).filter((r) =>
        isRiderProcessing(
          r.status,
          r.confidence_scores,
          r.extraction_step,
        ),
      ),
    [riders],
  );

  // Elapsed timer for processing section
  useEffect(() => {
    if (processingRiders.length > 0) {
      if (extractingStartRef.current === null) {
        extractingStartRef.current = Date.now();
      }
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          const start = extractingStartRef.current;
          if (!start) return;
          const diff = Math.floor((Date.now() - start) / 1000);
          const mins = String(Math.floor(diff / 60)).padStart(2, "0");
          const secs = String(diff % 60).padStart(2, "0");
          setElapsed(`${mins}:${secs}`);
        }, 1000);
      }
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      extractingStartRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [processingRiders.length]);

  const processingItems: ProcessingItemData[] = useMemo(
    () => processingRiders.map(riderToProcessingItem),
    [processingRiders],
  );

  const processingCounts = useMemo(() => ({
    uploading: 0,
    extracting: processingRiders.length,
    complete: 0,
    failed: 0,
    total: processingRiders.length,
  }), [processingRiders.length]);

  const deleteRiderMutation = useMutation({
    mutationFn: deleteRider,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["riders"] });
      toast.success("Rider deleted");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => batchDeleteRiders(ids),
    onSuccess: (res) => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["riders"] });
      if (res.deleted < res.total) {
        toast.warning(
          `Deleted ${res.deleted} of ${res.total} — ${res.total - res.deleted} skipped (no permission)`,
        );
      } else {
        toast.success(`Deleted ${res.deleted} rider${res.deleted === 1 ? "" : "s"}`);
      }
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const bulkApproveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      return batchApproveRiders(ids);
    },
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["riders"] });
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      updateRider(id, { artist_name: name }),
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["riders"] });
    },
  });

  const downloadOriginalMutation = useMutation({
    mutationFn: (rider: RiderListItem) =>
      downloadOriginalRiderPdf(
        rider.id,
        rider.original_filename ?? `${rider.artist_name}.pdf`,
      ),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Download failed"),
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredRiders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredRiders.map((r) => r.id)));
    }
  };

  const requestDelete = (ids: string[], names: string[]) => {
    setDeleteTarget({ ids, names });
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const close = { onSuccess: () => setDeleteOpen(false) };
    const [first] = deleteTarget.ids;
    if (deleteTarget.ids.length === 1 && first) {
      deleteRiderMutation.mutate(first, close);
    } else {
      bulkDeleteMutation.mutate(deleteTarget.ids, close);
    }
  };

  const deletePending = deleteRiderMutation.isPending || bulkDeleteMutation.isPending;

  const startRename = (id: string, currentName: string) => {
    setEditingId(id);
    setEditName(currentName);
  };

  const confirmRename = () => {
    if (editingId && editName.trim()) {
      renameMutation.mutate({ id: editingId, name: editName.trim() });
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  };

  const getConfidence = (r: RiderListItem) =>
    r.confidence_scores ? r.confidence_scores.overall ?? 0 : 0;

  const filteredRiders = useMemo(() => {
    if (!riders) return [];
    const q = search.toLowerCase();
    let result = riders.filter((r) =>
      matchesRiderStatusFilter(
        r.status,
        r.confidence_scores,
        statusFilter,
        r.extraction_step,
      ),
    );
    if (q) {
      result = result.filter(
        (r) =>
          r.artist_name.toLowerCase().includes(q) ||
          r.rider_type.toLowerCase().includes(q) ||
          (r.original_filename ?? "").toLowerCase().includes(q)
      );
    }
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "artist_name": cmp = a.artist_name.localeCompare(b.artist_name); break;
        case "rider_type": cmp = a.rider_type.localeCompare(b.rider_type); break;
        case "status": cmp = riderDisplayStatus(a).localeCompare(riderDisplayStatus(b)); break;
        case "confidence": cmp = getConfidence(a) - getConfidence(b); break;
        case "created_at": cmp = (a.created_at ?? "").localeCompare(b.created_at ?? ""); break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return result;
  }, [riders, search, sortKey, sortDir, statusFilter]);

  // Summary tiles (over the full set, not the filtered view)
  const tiles = useMemo(() => {
    const all = riders ?? [];
    return {
      needReview: all.filter(
        (r) =>
          r.status === "extracted" &&
          !isRiderProcessing(
            r.status,
            r.confidence_scores,
            r.extraction_step,
          ),
      ).length,
      approved: all.filter((r) => r.status === "approved").length,
      processing: all.filter((r) =>
        isRiderProcessing(
          r.status,
          r.confidence_scores,
          r.extraction_step,
        ),
      ).length,
      total: all.length,
    };
  }, [riders]);

  // Reset animation when filter changes so stagger replays
  useEffect(() => {
    animatedRef.current = false;
    const el = tbodyRef.current;
    if (el) el.setAttribute("data-animate-pending", "");
  }, [statusFilter]);

  // Stagger entrance for table rows on data load / filter change
  useEffect(() => {
    const el = tbodyRef.current;
    if (!el || animatedRef.current || filteredRiders.length === 0) return;
    animatedRef.current = true;

    const rows = el.children;
    anime.set(rows, { translateY: 8 });
    for (const row of Array.from(rows)) {
      (row as HTMLElement).style.willChange = "transform, opacity";
    }
    el.removeAttribute("data-animate-pending");

    anime({
      targets: rows,
      opacity: [0, 1],
      translateY: [8, 0],
      easing: "easeOutCubic",
      duration: 350,
      delay: anime.stagger(30, { start: 80 }),
      complete: () => {
        for (const row of Array.from(rows)) {
          const r = row as HTMLElement;
          r.style.willChange = "";
          r.style.opacity = "";
          r.style.transform = "";
        }
      },
    });
  }, [filteredRiders.length > 0, statusFilter]);

  return (
    <div className="space-y-6">
      <RidersWorkspaceHeader busy={isFetching && !isLoading} />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Need review" value={tiles.needReview} color="var(--tr-warn)" />
        <Tile label="Approved" value={tiles.approved} color="var(--tr-ok)" />
        <Tile label="Processing" value={tiles.processing} color="var(--tr-info)" />
        <Tile label="Total" value={tiles.total} color="var(--tr-text-2)" />
      </div>

      {/* Processing section — shown when riders are extracting */}
      {processingItems.length > 0 && (
        <div className="space-y-3">
          <ProcessingHeader counts={processingCounts} elapsed={elapsed} isComplete={false} />
          <div className="space-y-2">
            {processingItems.map((item, i) => (
              <ProcessingItem key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Filter + search + bulk actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            value={statusFilter}
            onChange={setStatusFilter}
            ariaLabel="Filter riders by status"
            options={STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
          />
          {riders && (
            <span className="text-xs text-ink-3">
              {filteredRiders.length}
              {filteredRiders.length !== riders.length ? ` / ${riders.length}` : ""} riders
            </span>
          )}
        </div>
        {selected.size > 0 ? (
          <div className="flex items-center gap-2">
            <Button
              variant="go"
              size="sm"
              icon={CheckCheck}
              onClick={() => bulkApproveMutation.mutate([...selected])}
              disabled={bulkApproveMutation.isPending}
            >
              {bulkApproveMutation.isPending ? "Approving…" : `Approve ${selected.size}`}
            </Button>
            <Button
              variant="bad"
              size="sm"
              icon={Trash2}
              onClick={() => {
                const rows = (riders ?? []).filter((r) => selected.has(r.id));
                requestDelete(rows.map((r) => r.id), rows.map((r) => r.artist_name));
              }}
              disabled={bulkDeleteMutation.isPending}
            >
              Delete {selected.size}
            </Button>
          </div>
        ) : (
          <div className="relative min-w-[220px] sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by artist or type…"
              className="pl-9"
            />
          </div>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <>
          <div className="rider-mobile-list md:hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rider-mobile-card">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
          <div className="card hidden overflow-hidden md:block">
          <table className="w-full text-sm">
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-line-2 last:border-0">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-28" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-12" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      ) : !riders || riders.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={FileText}
            title="No riders yet"
            subtitle="Upload a technical rider to start extracting."
            action={
              <Link to="/riders/upload" className="btn btn--accent btn--sm">
                Upload a rider
              </Link>
            }
          />
        </div>
      ) : (
        <>
          <div className="rider-mobile-list md:hidden">
            {filteredRiders.map((r) => {
              const confidence = getConfidence(r) || undefined;
              const isEditing = editingId === r.id;
              return (
                <article
                  key={r.id}
                  className={`rider-mobile-card ${selected.has(r.id) ? "rider-mobile-card--selected" : ""}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.artist_name}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      className="mt-1 rounded border-line-3 bg-panel-3 accent-[var(--tr-accent)]"
                    />
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") confirmRename();
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            autoFocus
                          />
                          <button onClick={confirmRename} className="rider-mobile-iconbtn text-ok" aria-label="Save name">
                            <Check className="h-4 w-4" />
                          </button>
                          <button onClick={() => setEditingId(null)} className="rider-mobile-iconbtn text-ink-3" aria-label="Cancel rename">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex min-w-0 items-start justify-between gap-2">
                            <Link to={`/riders/${r.id}/review`} className="min-w-0 text-[15px] font-semibold leading-tight text-ink">
                              <span className="block truncate">{r.artist_name}</span>
                            </Link>
                            <RiderStatusBadge status={riderDisplayStatus(r)} />
                          </div>
                          {r.original_filename && (
                            <p className="mt-1 truncate font-mono text-[10.5px] text-ink-4" title={r.original_filename}>
                              {r.original_filename}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="rider-mobile-meta">
                    <span className="tag tag--type">{r.rider_type}</span>
                    {confidence !== undefined ? <ConfidenceChip value={confidence} /> : <span className="text-ink-4">No confidence</span>}
                    <span>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "No date"}</span>
                  </div>

                  <div className="rider-mobile-actions">
                    <Link to={`/riders/${r.id}/review`} className="btn btn--accent btn--sm">
                      Review
                    </Link>
                    <button
                      onClick={() => downloadOriginalMutation.mutate(r)}
                      disabled={!r.original_pdf_available || downloadOriginalMutation.isPending}
                      className={`rider-mobile-iconbtn text-ink-4 ${r.original_pdf_available ? "" : "opacity-40"}`}
                      title={
                        r.original_pdf_available
                          ? "Download original PDF"
                          : "Original PDF not available"
                      }
                      aria-label={
                        r.original_pdf_available
                          ? `Download original PDF for ${r.artist_name}`
                          : `Original PDF unavailable for ${r.artist_name}`
                      }
                    >
                      {r.original_pdf_available ? (
                        <Download className="h-4 w-4" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => startRename(r.id, r.artist_name)}
                      className="rider-mobile-iconbtn"
                      aria-label={`Rename ${r.artist_name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => requestDelete([r.id], [r.artist_name])}
                      className="rider-mobile-iconbtn text-ink-4"
                      aria-label={`Delete ${r.artist_name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="card hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-2 bg-panel-2">
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={filteredRiders.length > 0 && selected.size === filteredRiders.length}
                    onChange={toggleAll}
                    className="rounded border-line-3 bg-panel-3 accent-[var(--tr-accent)]"
                  />
                </th>
                <SortHeader label="Artist" sortKey="artist_name" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortHeader label="Type" sortKey="rider_type" current={sortKey} dir={sortDir} onSort={toggleSort} hideOnMobile />
                <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                <SortHeader label="Confidence" sortKey="confidence" current={sortKey} dir={sortDir} onSort={toggleSort} hideOnMobile />
                <SortHeader label="Updated" sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort} hideOnMobile />
                <th className="w-12" />
              </tr>
            </thead>
            <tbody ref={tbodyRef} data-animate-pending="">
              {filteredRiders.map((r) => {
                const confidence = getConfidence(r) || undefined;
                const isEditing = editingId === r.id;
                return (
                  <tr
                    key={r.id}
                    className={`group border-b border-line-2 transition-colors last:border-0 hover:bg-panel-2 ${
                      selected.has(r.id) ? "bg-panel-2" : ""
                    }`}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.artist_name}`}
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="rounded border-line-3 bg-panel-3 accent-[var(--tr-accent)]"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") confirmRename();
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            autoFocus
                            className="h-7 py-0.5"
                          />
                          <button onClick={confirmRename} className="rounded p-0.5 text-ok hover:bg-panel-3" aria-label="Save name">
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setEditingId(null)} className="rounded p-0.5 text-ink-3 hover:bg-panel-3" aria-label="Cancel rename">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Link to={`/riders/${r.id}/review`} className="font-medium text-ink hover:text-accent">
                              {r.artist_name}
                            </Link>
                            <button
                              onClick={() => startRename(r.id, r.artist_name)}
                              className="rounded p-0.5 text-ink-4 opacity-0 transition-opacity hover:text-ink-2 group-hover:opacity-100"
                              title="Rename"
                              aria-label={`Rename ${r.artist_name}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </div>
                          {r.original_filename && (
                            <div
                              className="mt-0.5 max-w-[280px] truncate font-mono text-[10px] text-ink-4"
                              title={r.original_filename}
                            >
                              {r.original_filename}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="hidden px-4 py-2.5 sm:table-cell">
                      <span className="tag tag--type">{r.rider_type}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <RiderStatusBadge status={riderDisplayStatus(r)} />
                    </td>
                    <td className="hidden px-4 py-2.5 sm:table-cell">
                      {confidence !== undefined ? (
                        <ConfidenceChip value={confidence} />
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                    <td className="hidden px-4 py-2.5 font-mono text-xs text-ink-3 sm:table-cell">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => downloadOriginalMutation.mutate(r)}
                          disabled={!r.original_pdf_available || downloadOriginalMutation.isPending}
                          className="rounded p-1 text-ink-4 opacity-0 transition-all hover:bg-panel-3 hover:text-ink-2 disabled:cursor-not-allowed disabled:opacity-40 group-hover:opacity-100"
                          title={
                            r.original_pdf_available
                              ? "Download original PDF"
                              : "Original PDF not available"
                          }
                          aria-label={
                            r.original_pdf_available
                              ? `Download original PDF for ${r.artist_name}`
                              : `Original PDF unavailable for ${r.artist_name}`
                          }
                        >
                          {r.original_pdf_available ? (
                            <Download className="h-3.5 w-3.5" />
                          ) : (
                            <FileText className="h-3.5 w-3.5" />
                          )}
                        </button>
                      <button
                        onClick={() => requestDelete([r.id], [r.artist_name])}
                        className="rounded p-1 text-ink-4 opacity-0 transition-all hover:bg-panel-3 hover:text-bad group-hover:opacity-100"
                        title="Delete rider"
                        aria-label={`Delete ${r.artist_name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        tone="danger"
        icon={Trash2}
        title={
          deleteTarget && deleteTarget.ids.length > 1
            ? `Delete ${deleteTarget.ids.length} riders?`
            : `Delete “${deleteTarget?.names[0] ?? "rider"}”?`
        }
        confirmLabel={
          deleteTarget && deleteTarget.ids.length > 1
            ? `Delete ${deleteTarget.ids.length} riders`
            : "Delete rider"
        }
        pendingLabel="Deleting…"
        pending={deletePending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      >
        <p>
          This permanently removes{" "}
          {deleteTarget && deleteTarget.ids.length > 1
            ? "these riders, their uploaded PDFs"
            : "this rider, its uploaded PDF"}{" "}
          and all extracted data. This can&apos;t be undone.
        </p>
        {deleteTarget && deleteTarget.ids.length > 1 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {deleteTarget.names.slice(0, 6).map((n, i) => (
              <span key={`${n}-${i}`} className="tag">
                {n}
              </span>
            ))}
            {deleteTarget.names.length > 6 && (
              <span className="tag">+{deleteTarget.names.length - 6} more</span>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}

function Tile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card p-4">
      <div className="font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3">
        {label}
      </div>
    </div>
  );
}

function SortHeader({
  label, sortKey: key, current, dir, onSort, hideOnMobile,
}: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir;
  onSort: (key: SortKey) => void;
  hideOnMobile?: boolean;
}) {
  const active = current === key;
  return (
    <th
      className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.06em] text-ink-4 ${
        hideOnMobile ? "hidden sm:table-cell" : ""
      }`}
    >
      <button onClick={() => onSort(key)} className="flex items-center gap-1 transition-colors hover:text-ink-2">
        {label}
        {active ? (
          dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </th>
  );
}
